import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../api/services/common/models";
import {
  FormController,
  MuiDateField,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  OptionsDataType,
} from "../../../../../components";
import { DATE_FORMAT, MAX_LENGTH_75 } from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { setApiDataToDropDownOption } from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";

interface ProgramSearchFieldsProps {
  pageId: string;
  dedicatedProjectManagerMappedData: OptionsDataType[];
  lineOfBusinessData: IdNameList[];
  filterDropdownValue: OptionsDataType[];
}
export const ProgramsSearchFields: FC<ProgramSearchFieldsProps> = ({
  pageId,
  dedicatedProjectManagerMappedData,
  lineOfBusinessData,
  filterDropdownValue,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { getValues, setValue, control } = useFormContext();

  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const { commonLabels, fetchDropdownValues, useFetchLabel, commonValidationMessages } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);

  const statusDropdownValue = fetchDropdownValues(
    programSearchSiteCoreKeys.searchFormKey,
    programSearchSiteCoreKeys.statusList,
  );

  const handleMuiDateChange = (newValue: dayjs.Dayjs | null) => {
    let dateValue = "";
    if (newValue?.isValid()) {
      dateValue = newValue.format(userCountryDateFormat);
    }
    setValue(ProgramSearchProgramFormKey.programStartMonthAndYear, dateValue);
  };

  return (
    <MuiGrid container className="form-container">
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.programName}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.ProgramNameLabel)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.programNameFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.programName}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.programNameFilter}`}
            maxLength={MAX_LENGTH_75}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={commonValidationMessages.fieldSpecialCharactersTooltipMessage}
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.programStatus}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.programStatusLabel)}
            options={statusDropdownValue}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${ProgramSearchProgramFormKey.programStatus}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.projectManager}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.projectManager}`}
            label={getLabel(programSearchSiteCoreKeys.projectManagerLabel)}
            requiredARIALabel={commonARIALabel.required}
            options={dedicatedProjectManagerMappedData}
            visibleDefaultSelectOption
            value=""
            placeholder={commonLabels.selectPlaceHolder}
            showSearch
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.lineOfBusiness}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.lineOfBusiness}`}
            label={getLabel(programSearchSiteCoreKeys.productLineLabel)}
            placeholder={commonLabels.selectPlaceHolder}
            showSearch
            value=""
            options={setApiDataToDropDownOption(lineOfBusinessData)}
            requiredARIALabel={commonARIALabel.required}
            visibleDefaultSelectOption
          />
        </FormController>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.programStartMonthAndYear}
        >
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{
              fieldMonthPlaceholder: () =>
                getLabel(programSearchSiteCoreKeys.monthPlaceholder),
              fieldYearPlaceholder: () =>
                getLabel(programSearchSiteCoreKeys.yearPlaceholder),
            }}
          >
            <MuiDateField
              id={`${pageId}-${ProgramSearchProgramFormKey.programStartMonthAndYear}`}
              value={
                getValues().programStartMonthAndYear
                  ? dayjs(getValues().programStartMonthAndYear)
                  : null
              }
              label={getLabel(
                programSearchSiteCoreKeys.programStartMonthOrYearLabel,
              )}
              format={DATE_FORMAT.MMM_YYYY_SLASH}
              views={["month", "year"]}
              onChange={handleMuiDateChange}
              clearable
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </LocalizationProvider>
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
